/**
 * Marker helpers: family grouping (legend toggles), stable ids, the
 * PriceMarker projection with selection / hover / pulse styling, and the
 * bot-style one-liner used while the full action has not been fetched.
 */
import type { Candle, Marker, MarkerKind } from "@/api/types";
import type { PriceMarker } from "@/components/PriceChart";
import { fmtAmount, fmtPrice, fmtUsd } from "@/lib/format";
import { KIND_LABEL, TIER_LABEL } from "@/lib/tier";

export type MarkerFamily = "buys" | "sells" | "transfers" | "liquidity" | "signals";

export const MARKER_FAMILIES: readonly MarkerFamily[] = [
  "buys",
  "sells",
  "liquidity",
  "transfers",
  "signals",
];

export const MARKER_FAMILY_LABEL: Record<MarkerFamily, string> = {
  buys: "▲ buys",
  sells: "▼ sells",
  transfers: "→ transfers",
  liquidity: "◆ liquidity",
  signals: "● signals",
};

export function markerFamily(kind: MarkerKind): MarkerFamily {
  switch (kind) {
    case "buy":
    case "mint":
      return "buys";
    case "sell":
    case "burn":
      return "sells";
    case "send":
    case "receive":
      return "transfers";
    case "add_liquidity":
    case "remove_liquidity":
    case "collect_fees":
      return "liquidity";
    case "holder_spike":
    case "accumulation":
    case "note":
      return "signals";
  }
}

/** Stable id: `<tx>:<address>` for actions, the signal id for signals. */
export function markerId(m: Marker): string {
  return m.signal ?? `${m.tx ?? "?"}:${m.address}`;
}

export function actionId(tx: string, address: string): string {
  return `${tx}:${address}`;
}

export interface ProjectOptions {
  bucket: number;
  candles: readonly Candle[];
  enabled: ReadonlySet<MarkerFamily>;
  selection: readonly string[];
  hoveredId?: string | null;
  pulseId?: string | null;
  /** Explicit buy/sell links stay visible even if their legend family is hidden. */
  relatedIds?: ReadonlySet<string>;
  priceInUsd?: boolean;
  colors: { accent: string; signal: string; selected: string };
  /** arrows stacked above / below a bar per side; the rest of the bucket is pinned at its price */
  stackLimit?: number;
}

const SIGNAL_GLYPH: Partial<Record<MarkerKind, string>> = {
  holder_spike: "H",
  accumulation: "🧲",
  note: "✎",
};

/**
 * Project server markers onto PriceChart markers. Signals ride the candle
 * (circle above the bar). Per bucket and side only the `stackLimit` largest
 * trades stack as arrows above / below the bar; the rest — and any marker in
 * a bucket without a candle — is pinned at its own trade price (dropped when
 * it has none), so stacks never hide the candles. Selected families are
 * drawn in the `selected` colour (the text colour: direction stays in the
 * arrow shape) and larger; when a selection exists the rest is dimmed by
 * leaving them unflagged (hollow).
 */
export function toPriceMarkers(markers: readonly Marker[], o: ProjectOptions): PriceMarker[] {
  const b = Math.max(1, o.bucket);
  const hasCandle = new Set(o.candles.map((c) => Math.floor(c.t / b) * b));
  const selected = new Set(o.selection.map((s) => s.toLowerCase()));
  const limit = o.stackLimit ?? 3;
  // rank markers within (bucket, side) by USD so only the largest stack as arrows
  const rank = new Map<Marker, number>();
  const groups = new Map<string, Marker[]>();
  for (const m of markers) {
    const fam = markerFamily(m.kind);
    if (fam === "signals" || fam === "liquidity") continue;
    const key = `${Math.floor(m.timestamp / b) * b}:${fam}`;
    let g = groups.get(key);
    if (!g) groups.set(key, (g = []));
    g.push(m);
  }
  for (const g of groups.values()) {
    g.sort((x, y) => (y.usd ?? 0) - (x.usd ?? 0));
    g.forEach((m, i) => rank.set(m, i));
  }
  const out: PriceMarker[] = [];
  for (const m of markers) {
    const fam = markerFamily(m.kind);
    const id = markerId(m);
    const isRelated = o.relatedIds?.has(id) ?? false;
    if (!o.enabled.has(fam) && !isRelated) continue;
    const t = Math.floor(m.timestamp / b) * b;
    const isSel = selected.size > 0 && selected.has(m.family.toLowerCase());
    const isHover = o.hoveredId === id;
    const isPulse = o.pulseId === id;
    const pm: PriceMarker = {
      time: t,
      kind: m.kind,
      tier: m.tier,
      flagged: selected.size > 0 ? isSel || m.flagged : m.flagged,
      id,
    };
    if (fam === "signals") {
      pm.text = SIGNAL_GLYPH[m.kind];
      pm.color = o.colors.signal;
      pm.size = 1.2;
    }
    if (isSel) {
      pm.color = o.colors.selected;
      pm.size = (pm.size ?? sizeOf(m)) * 1.4;
      pm.flagged = true;
    }
    if (isHover || isPulse) {
      pm.size = (pm.size ?? sizeOf(m)) * (isPulse ? 2.2 : 1.6);
      pm.flagged = true;
    }
    if (isRelated) {
      pm.color = o.colors.accent;
      pm.size = Math.max(pm.size ?? sizeOf(m), 1.8);
      pm.flagged = true;
      pm.text = m.kind === "buy" ? "BUY" : "SELL";
      if (o.priceInUsd !== false && m.price_usd !== undefined) pm.price = m.price_usd;
    }
    const overflow = (rank.get(m) ?? 0) >= limit && !isSel && !isHover && !isPulse && !isRelated;
    if ((!hasCandle.has(t) || overflow) && fam !== "signals") {
      // A liquidity quote is a ratio of deposited/withdrawn assets, not an
      // execution price. Fee collections in particular may contain token
      // dust and would stretch the entire price axis to astronomical values.
      const executionPrice =
        o.priceInUsd !== false && (m.kind === "buy" || m.kind === "sell") ? m.price_usd : undefined;
      if (executionPrice === undefined) {
        if (!hasCandle.has(t)) continue;
      } else {
        pm.price = executionPrice;
        if (overflow) pm.size = (pm.size ?? sizeOf(m)) * 0.6;
      }
    }
    out.push(pm);
  }
  return out;
}

function sizeOf(m: Marker): number {
  switch (m.tier) {
    case "whale":
      return 2.4;
    case "big":
      return 1.6;
    case "small":
      return 1;
    default:
      return 0.6;
  }
}

/** Bot-style one-liner from the marker alone (the server's `text` replaces it once fetched). */
export function markerLine(m: Marker, symbol: string): string {
  const fam = markerFamily(m.kind);
  if (fam === "signals") {
    const what =
      m.kind === "holder_spike"
        ? "holder spike"
        : m.kind === "accumulation"
          ? "accumulation verdict"
          : "note";
    return `${what} · ${m.name}${m.usd !== undefined ? ` · ${fmtUsd(m.usd)}` : ""}`;
  }
  const tier = m.tier !== "none" ? `${TIER_LABEL[m.tier].toUpperCase()} ` : "";
  const kind = KIND_LABEL[m.kind as keyof typeof KIND_LABEL] ?? m.kind;
  const parts = [`${tier}${kind}`, m.name];
  if (m.amount !== undefined) parts.push(fmtAmount(m.amount, symbol));
  if (m.usd !== undefined) parts.push(`(${fmtUsd(m.usd)})`);
  if (m.price_usd !== undefined) parts.push(`@ ${fmtPrice(m.price_usd)}`);
  if (m.flagged) parts.push("⚑");
  return parts.join(" ");
}
